/* AccountBilling — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Route: `/account/billing`. Auth-gated. Shows the current subscription
 * summary and routes everything else (update card, view invoices, cancel/
 * resume) through the Stripe Customer Portal via
 * POST /api/payments/customer-portal-session.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreditCard, ExternalLink, ArrowRight, ShieldCheck, PlayCircle, XCircle, RefreshCw, Award, AlertTriangle, Pause } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import CancelFlowModal from "@/pages/CancelFlowModal";
import AppNav from "@/components/app/AppNav";
import { V2Button } from "@/components/v2/FormPrimitives";

const STATUS_TONES = {
  trialing: "bg-[var(--ds-color-warning-soft)] text-[var(--ds-color-warning)]",
  active: "bg-[var(--ds-color-success-soft)] text-[var(--ds-color-success)]",
  past_due: "bg-[var(--ds-color-danger-soft)] text-[var(--ds-color-danger)]",
  canceled: "bg-[var(--ds-color-surface-muted)] text-[var(--ds-color-ink-muted)]",
  incomplete: "bg-[var(--ds-color-warning-soft)] text-[var(--ds-color-warning)]",
  incomplete_expired: "bg-[var(--ds-color-surface-muted)] text-[var(--ds-color-ink-muted)]",
  paused: "bg-[var(--ds-color-info-soft)] text-[var(--ds-color-info)]",
};

function StatusPill({ status }) {
  const tone = STATUS_TONES[status] || "bg-[var(--ds-color-surface-muted)] text-[var(--ds-color-ink-muted)]";
  return (
    <span
      data-testid="billing-status-pill"
      className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 ${tone}`}
    >
      {status || "unknown"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

async function trackEvent(eventName, props) {
  try {
    await api.post("/events", { event_name: eventName, properties: props || {} });
  } catch {
    /* swallow */
  }
}

export default function AccountBilling() {
  const { user } = useAuth();
  const isFoundingMember = Array.isArray(user?.badges) && user.badges.includes("founding_member");
  const [showCancelFlow, setShowCancelFlow] = useState(false);
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (params.get("portal_return") === "true") {
        try {
          const { data: sync } = await api.post("/payments/sync-subscription");
          if (sync?.changes && sync.changes.status?.after === "canceled") {
            toast.success("Subscription canceled — synced from Stripe.");
          } else if (sync?.changes && sync.changes.cancel_at_period_end?.after === true) {
            toast.success("Cancellation scheduled at period end — synced from Stripe.");
          } else if (sync?.synced) {
            toast.success("Subscription updated — synced from Stripe.");
          }
        } catch (e) {
          console.warn("Portal-return sync failed:", formatApiError(e));
        }
        const next = new URLSearchParams(params);
        next.delete("portal_return");
        setParams(next, { replace: true });
      }
      const { data: d } = await api.get("/payments/my-subscription");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [params, setParams]);

  useEffect(() => {
    load();
    trackEvent("Billing Page Viewed", {});
  }, [load]);

  async function openPortal(flow = null) {
    setPortalBusy(true);
    try {
      await trackEvent(
        flow === "cancel" ? "Cancel Subscription Clicked" : "Manage Subscription Clicked",
        { flow: flow || "default" },
      );
      const { data: d } = await api.post(
        "/payments/customer-portal-session",
        {},
        { params: { origin_url: window.location.origin, ...(flow ? { flow } : {}) } },
      );
      window.location.href = d.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPortalBusy(false);
    }
  }

  async function resumeSubscription() {
    setPortalBusy(true);
    try {
      await trackEvent("Resume Subscription Clicked", {});
      await api.post("/payments/resume-subscription");
      toast.success("Subscription resumed. Your trial will convert to paid as scheduled.");
      const { data: d } = await api.get("/payments/my-subscription");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPortalBusy(false);
    }
  }

  if (loading) {
    return (
      <div
        data-testid="account-billing-loading"
        className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)] flex items-center justify-center"
      >
        <span className="text-[13px] text-[var(--ds-color-ink-muted)]">Loading…</span>
      </div>
    );
  }

  const sub = data?.subscription;
  const plan = data?.plan;
  const hasRealSub = !!sub && !sub.is_stub && sub.plan_code !== "free";
  const billingCycle = sub?.billing_cycle || "monthly";
  const subStatus = sub?.status || "free";
  const monthlyCents = Number(plan?.price_monthly_cents || 0);
  const annualDiscount = Number(plan?.annual_discount_percent ?? 20);
  const displayAmount =
    billingCycle === "annual"
      ? Math.round(monthlyCents * 12 * (1 - annualDiscount / 100))
      : monthlyCents;

  return (
    <div data-testid="account-billing-page" className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]">
      <AppNav />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] uppercase tracking-[0.18em] text-[var(--ds-color-accent)]">
              Account
            </div>
            <h1
              className="text-[28px] font-semibold mt-2"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              Billing
            </h1>
            {isFoundingMember && (
              <div
                data-testid="account-billing-founding-badge"
                title="You're one of our first 100 Premium members — thank you for helping build Informed OS"
                className="inline-flex items-center gap-1.5 mt-3 rounded-full px-3 py-1 text-[13px] font-medium bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent)] border border-[var(--ds-color-accent)]/40"
              >
                <Award className="w-3 h-3" /> Founding Member
              </div>
            )}
          </div>
          <Link
            to="/dashboard"
            data-testid="account-billing-back"
            className="text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
          >
            ← Back to dashboard
          </Link>
        </div>

        {hasRealSub && sub?.pause_collection && (
          <div
            data-testid="account-billing-paused-banner"
            className="mb-6 rounded-[var(--ds-radius-lg)] px-4 py-3 flex items-start justify-between gap-4 flex-wrap bg-[var(--ds-color-info-soft)] border border-[var(--ds-color-info)]/40 text-[var(--ds-color-info)]"
          >
            <div className="flex items-start gap-3">
              <Pause className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="text-[13px] font-semibold">Your subscription is paused</div>
                <div className="text-[13px] opacity-90 mt-1 leading-relaxed">
                  We won't charge you during the pause. Access is limited to what you already had.
                  {sub?.pause_collection?.resumes_at
                    ? ` Auto-resumes ${formatDate(new Date(sub.pause_collection.resumes_at * 1000).toISOString())}.`
                    : ""}
                </div>
              </div>
            </div>
            <button
              data-testid="account-billing-resume-from-pause-btn"
              disabled={portalBusy}
              onClick={async () => {
                setPortalBusy(true);
                try {
                  await api.post("/payments/resume-subscription-from-pause");
                  toast.success("Your subscription is active again.");
                  await load();
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setPortalBusy(false);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-3 py-1.5 text-[13px] font-semibold bg-[var(--ds-color-info)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <PlayCircle className="w-3.5 h-3.5" /> Resume now
            </button>
          </div>
        )}

        {hasRealSub && (subStatus === "past_due" || subStatus === "incomplete") && (
          <div
            data-testid="account-billing-past-due-banner"
            className="mb-6 rounded-[var(--ds-radius-lg)] px-4 py-3 flex items-start justify-between gap-4 flex-wrap bg-[var(--ds-color-warning-soft)] border border-[var(--ds-color-warning)]/40 text-[var(--ds-color-warning)]"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="text-[13px] font-semibold">Payment issue on your account</div>
                <div className="text-[13px] opacity-90 mt-1 leading-relaxed">
                  Your last payment couldn't be processed. Update your payment method now to
                  keep your access.
                </div>
              </div>
            </div>
            <button
              data-testid="account-billing-update-payment-btn"
              disabled={portalBusy}
              onClick={() => openPortal("payment_method_update")}
              className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-3 py-1.5 text-[13px] font-semibold bg-[var(--ds-color-warning)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <CreditCard className="w-3.5 h-3.5" /> Update payment method
            </button>
          </div>
        )}

        {hasRealSub ? (
          <div
            data-testid="account-billing-current-card"
            className="rounded-[var(--ds-radius-xl)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-accent)]/40 shadow-[var(--ds-shadow-card)] p-6 md:p-8 mb-8"
          >
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] uppercase tracking-[0.18em] text-[var(--ds-color-accent)] mb-1">
                  Current subscription
                </div>
                <h2
                  data-testid="account-billing-plan-name"
                  className="text-[22px] font-semibold text-[var(--ds-color-ink)]"
                  style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                >
                  {plan?.name || sub?.plan_code}
                </h2>
              </div>
              <StatusPill status={subStatus} />
            </div>
            <p
              data-testid="account-billing-amount"
              className="text-[14px] text-[var(--ds-color-ink-muted)] mb-4"
            >
              ${(displayAmount / 100).toFixed(2)} /{" "}
              {billingCycle === "annual" ? "year" : "month"} ·{" "}
              <span className="text-[var(--ds-color-ink-subtle)]">{billingCycle}</span>
            </p>
            <div className="grid grid-cols-2 gap-4 text-[13px] mb-5">
              <div>
                <div className="text-[var(--ds-color-ink-subtle)] mb-0.5">Next billing date</div>
                <div data-testid="account-billing-next-date" className="text-[var(--ds-color-ink)]">
                  {formatDate(sub?.current_period_end)}
                </div>
              </div>
              {sub?.trial_end ? (
                <div>
                  <div className="text-[var(--ds-color-ink-subtle)] mb-0.5">Trial ends</div>
                  <div data-testid="account-billing-trial-end" className="text-[var(--ds-color-ink)]">
                    {formatDate(sub.trial_end)}
                  </div>
                </div>
              ) : null}
              {sub?.cancel_at_period_end && (
                <div className="col-span-2">
                  <span data-testid="account-billing-cancel-pending" className="text-[var(--ds-color-warning)]">
                    Subscription canceled — active until {formatDate(sub?.current_period_end)}
                  </span>
                </div>
              )}
              {subStatus === "canceled" && !sub?.cancel_at_period_end && (
                <div className="col-span-2">
                  <span data-testid="account-billing-canceled" className="text-[var(--ds-color-ink-muted)]">
                    Subscription canceled
                    {sub?.canceled_at ? ` on ${formatDate(sub.canceled_at)}` : ""}
                  </span>
                </div>
              )}
            </div>
            {(() => {
              const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
              const periodExpired = periodEnd && periodEnd.getTime() < Date.now();
              const activeStatus = subStatus === "active" || subStatus === "trialing";
              const cancelScheduled = activeStatus && sub?.cancel_at_period_end === true && !periodExpired;
              const fullyCanceled = subStatus === "canceled" || (periodExpired && !activeStatus);

              if (activeStatus && !cancelScheduled) {
                return (
                  <div className="flex flex-wrap gap-3 items-center">
                    <V2Button
                      data-testid="account-billing-manage-btn"
                      disabled={portalBusy}
                      onClick={() => openPortal(null)}
                    >
                      <CreditCard className="w-4 h-4" />
                      {portalBusy ? "Opening portal…" : "Manage subscription"}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </V2Button>
                    <button
                      data-testid="account-billing-cancel-btn"
                      disabled={portalBusy}
                      onClick={() => {
                        trackEvent("Cancel Flow Opened", { plan_code: sub?.plan_code });
                        setShowCancelFlow(true);
                      }}
                      className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2.5 text-[13px] font-semibold border border-[var(--ds-color-border)] text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)] transition-colors disabled:opacity-60"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel subscription
                    </button>
                  </div>
                );
              }

              if (cancelScheduled) {
                return (
                  <div className="flex flex-col gap-3">
                    <V2Button
                      data-testid="account-billing-resume-btn"
                      disabled={portalBusy}
                      onClick={resumeSubscription}
                      className="self-start"
                    >
                      <PlayCircle className="w-4 h-4" />
                      {portalBusy ? "Resuming…" : "Resume subscription"}
                    </V2Button>
                    <p className="text-[13px] text-[var(--ds-color-ink-muted)] leading-relaxed max-w-md">
                      Your subscription is scheduled to cancel on{" "}
                      <span className="text-[var(--ds-color-ink)]">
                        {formatDate(sub?.current_period_end)}
                      </span>
                      . Resume anytime before then to continue uninterrupted.
                    </p>
                    <button
                      data-testid="account-billing-manage-btn"
                      disabled={portalBusy}
                      onClick={() => openPortal(null)}
                      className="text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors self-start"
                    >
                      Manage subscription →
                    </button>
                  </div>
                );
              }

              if (fullyCanceled) {
                return (
                  <V2Button
                    as={Link}
                    to="/pricing"
                    data-testid="account-billing-resubscribe-btn"
                    className="self-start"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Re-subscribe
                  </V2Button>
                );
              }

              return (
                <V2Button
                  data-testid="account-billing-manage-btn"
                  disabled={portalBusy}
                  onClick={() => openPortal(null)}
                >
                  <CreditCard className="w-4 h-4" />
                  {portalBusy ? "Opening portal…" : "Manage subscription"}
                </V2Button>
              );
            })()}
            <p
              data-testid="account-billing-portal-note"
              className="text-[13px] text-[var(--ds-color-ink-subtle)] mt-4 leading-relaxed"
            >
              The Customer Portal lets you update your payment method, view invoices, swap plans,
              pause, or cancel — all through Stripe.
            </p>
          </div>
        ) : (
          <div
            data-testid="account-billing-no-subscription"
            className="rounded-[var(--ds-radius-xl)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] shadow-[var(--ds-shadow-card)] p-6 md:p-8 mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-[var(--ds-color-accent)]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] uppercase tracking-[0.18em] text-[var(--ds-color-accent)]">
                Free tier
              </span>
            </div>
            <h2
              className="text-[22px] font-semibold mb-2"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              You're on the free tier
            </h2>
            <p className="text-[14px] text-[var(--ds-color-ink-muted)] mb-5 leading-relaxed">
              Upgrade any time to unlock AI credit analysis, dispute letter drafting, funding
              partner matching, and the full readiness playbook.
            </p>
            <V2Button
              as={Link}
              to="/pricing"
              data-testid="account-billing-subscribe"
            >
              Subscribe to a plan <ArrowRight className="w-3.5 h-3.5" />
            </V2Button>
          </div>
        )}

        <p
          data-testid="account-billing-disclaimer"
          className="text-[13px] text-[var(--ds-color-ink-subtle)] mt-10 max-w-xl leading-relaxed"
        >
          Charges are billed by Informed OS via Stripe. Cancel anytime. Subscription
          does not guarantee funding approval or credit improvement.
        </p>
      </main>
      <CancelFlowModal
        isOpen={showCancelFlow}
        onClose={() => setShowCancelFlow(false)}
        onDone={() => { setShowCancelFlow(false); load(); }}
      />
    </div>
  );
}
